import { Alert, Logo, cn } from '@zuruflex/ui';
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface AuthCardProps {
  title: string;
  subtitle?: string;
  /** Shown above the form, in red. Already translated. */
  error?: string | null;
  /** Shown above the form, in blue. For "check your inbox" and its kin. */
  notice?: ReactNode;
  children: ReactNode;
  /** The "new here? / already have an account?" line under the card. */
  footer?: ReactNode;
  className?: string;
}

/**
 * The frame every auth screen sits in.
 *
 * Deliberately outside `SiteLayout`: these pages are one question with one
 * answer, and the header's search pill and the footer's twenty links are
 * both invitations to go and do something else. The review form is
 * arranged the same way and for the same reason.
 *
 * The logo goes home rather than nowhere, so somebody who landed here by
 * accident — an expired link, a mistyped URL — has an obvious way back to
 * the fleet instead of a dead end.
 */
export function AuthCard({
  title,
  subtitle,
  error,
  notice,
  children,
  footer,
  className,
}: AuthCardProps) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-surface px-6 py-12">
      <div className={cn('w-full max-w-sm', className)}>
        <Link to="/" aria-label="Zuruflex" className="inline-block text-text">
          <Logo className="h-8" />
        </Link>

        <h1 className="mt-10 font-display text-2xl font-bold tracking-tight text-text">{title}</h1>
        {subtitle ? (
          <p className="mt-2 text-sm leading-relaxed text-text-muted">{subtitle}</p>
        ) : null}

        {error ? (
          <Alert variant="danger" className="mt-6">
            {error}
          </Alert>
        ) : null}
        {notice ? (
          <Alert variant="info" className="mt-6">
            {notice}
          </Alert>
        ) : null}

        <div className="mt-6">{children}</div>

        {footer ? <div className="mt-8 text-center text-sm text-text-muted">{footer}</div> : null}
      </div>
    </main>
  );
}

/** "or" between the password form and the Google button. */
export function OrDivider({ label }: { label: string }) {
  return (
    <div className="my-6 flex items-center gap-3">
      <span className="h-px flex-1 bg-border-default" />
      <span className="text-xs uppercase tracking-wide text-text-muted">{label}</span>
      <span className="h-px flex-1 bg-border-default" />
    </div>
  );
}
